import { Component, inject, OnInit, OnDestroy, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { Header } from '../../components/header/header';
import { Footer } from '../../components/footer/footer';
import cloudflareConfig from '../../core/config/cloudflare.config';
import { MetaService } from '../../core/services/meta.service';

declare global {
  interface Window {
    turnstile: any;
  }
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, Header, Footer],
  templateUrl: './login.html',
  styleUrls: ['./login.css']
})
export class LoginComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private cdr = inject(ChangeDetectorRef);
  private metaService = inject(MetaService);
  private isBrowser: boolean;

  email = '';
  password = '';
  isLoading = false;
  errorMessage = '';
  
  // Cloudflare Turnstile
  turnstileToken: string | null = null;
  turnstileWidgetId: string | null = null;
  turnstileSiteKey: string = cloudflareConfig.turnstile.Site_Key;
  turnstileLoaded = false;

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.metaService.resetToDefault();

    if (this.authService.isAuthenticated()) {
      this.router.navigate(['/profile']);
      return;
    }

    // Renderizar Cloudflare Turnstile (el script ya está cargado en index.html)
    if (this.isBrowser) {
      // Esperar a que Turnstile esté disponible
      this.waitForTurnstile();
    }
  }

  ngOnDestroy(): void {
    // Limpiar el widget de Turnstile
    if (this.isBrowser && this.turnstileWidgetId && window.turnstile) {
      try {
        window.turnstile.remove(this.turnstileWidgetId);
      } catch (error) {
        console.error('Error al limpiar Turnstile:', error);
      }
    }
  }

  private waitForTurnstile(): void {
    if (!this.isBrowser) return;

    // Verificar si Turnstile ya está disponible
    if (window.turnstile) {
      this.renderTurnstile();
      return;
    }

    // Esperar a que Turnstile se cargue (máximo 5 segundos)
    let attempts = 0;
    const maxAttempts = 50; // 50 intentos * 100ms = 5 segundos
    const checkInterval = setInterval(() => {
      attempts++;
      if (window.turnstile) {
        clearInterval(checkInterval);
        this.renderTurnstile();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        console.error('Timeout esperando Cloudflare Turnstile');
        this.errorMessage = 'Error al cargar el sistema de verificación';
      }
    }, 100);
  }

  private renderTurnstile(): void {
    if (!this.isBrowser || !window.turnstile) return;

    const container = document.getElementById('turnstile-container');
    if (!container) {
      console.error('Contenedor de Turnstile no encontrado');
      return;
    }

    try {
      this.turnstileWidgetId = window.turnstile.render('#turnstile-container', {
        sitekey: this.turnstileSiteKey,
        theme: 'light',
        callback: (token: string) => {
          console.log('✅ Turnstile verificado correctamente');
          this.turnstileToken = token;
        },
        'error-callback': () => {
          console.error('❌ Error en la verificación de Turnstile');
          this.turnstileToken = null;
        },
        'expired-callback': () => {
          console.warn('⚠️ Token de Turnstile expirado');
          this.turnstileToken = null;
        }
      });
    } catch (error) {
      console.error('Error al renderizar Turnstile:', error);
    }
  }

  onSubmit(): void {
    if (!this.email || !this.password) {
      this.errorMessage = 'Por favor complete todos los campos';
      return;
    }

    // Validar token de Turnstile
    if (!this.turnstileToken) {
      this.errorMessage = 'Por favor complete la verificación de seguridad';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    this.authService.login({
      email: this.email,
      password: this.password,
      turnstileToken: this.turnstileToken
    }).subscribe({
      next: (response) => {
        if (response.success) {
          // Redirigir al home o a la página anterior
          this.router.navigate(['/']);
        } else {
          this.errorMessage = response.message || 'Error al iniciar sesión';
          console.log('❌ Error de login (respuesta):', this.errorMessage);
          // Resetear Turnstile en caso de error
          this.resetTurnstile();
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('❌ Error completo en login:', JSON.stringify(error, null, 2));
        
        // Resetear estado
        this.isLoading = false;
        
        // Intentar extraer el mensaje de error
        let errorMsg = 'Usuario o contraseña incorrectos';
        
        // El backend envía el error en error.error.message
        if (error && error.error && error.error.message) {
          errorMsg = error.error.message;
        } else if (error && error.message) {
          errorMsg = error.message;
        }
        
        console.log('📝 Mensaje de error final:', errorMsg);
        this.errorMessage = errorMsg;
        
        // Resetear Turnstile
        this.resetTurnstile();
        
        // Forzar detección de cambios
        this.cdr.detectChanges();
      }
    });
  }

  private resetTurnstile(): void {
    if (this.isBrowser && this.turnstileWidgetId && window.turnstile) {
      try {
        window.turnstile.reset(this.turnstileWidgetId);
        this.turnstileToken = null;
      } catch (error) {
        console.error('Error al resetear Turnstile:', error);
      }
    }
  }
}
